import * as Sentry from '@sentry/react';
import { createBrowserRouter, Navigate, useLocation } from 'react-router';
import type { RouteObject } from 'react-router';
import { useAuth } from '@/contexts/AuthContext';
import { AuthGuard } from '@/guards/AuthGuard';
import { AdminGuard } from '@/guards/AdminGuard';
import { GuestGuard } from '@/guards/GuestGuard';
import { AdminGuestGuard } from '@/guards/AdminGuestGuard';
import { CrossHostNavigate } from '@/components/CrossHostNavigate';
import {
  currentHostRole,
  type HostRole,
  stripLegacyOwnerPrefix,
  stripLegacyAdminPrefix,
} from '@/lib/hosts';

export function LegacyOwnerRedirect() {
  const location = useLocation();
  const stripped = stripLegacyOwnerPrefix(location.pathname);
  return <Navigate to={`${stripped}${location.search}${location.hash}`} replace />;
}

export function LegacyAdminRedirect() {
  const location = useLocation();
  const stripped = stripLegacyAdminPrefix(location.pathname);
  return <Navigate to={`${stripped}${location.search}${location.hash}`} replace />;
}

function DefaultRedirect() {
  const { isAuthenticated, isAdmin, user } = useAuth();
  if (!isAuthenticated) return <CrossHostNavigate to="/login" replace />;
  if (isAdmin) return <CrossHostNavigate to="/admin" replace />;
  if (user && (user.needs_onboarding || (user.shops?.length ?? 0) === 0))
    return <CrossHostNavigate to="/owner/onboarding" replace />;
  return <CrossHostNavigate to="/owner/dashboard" replace />;
}

// Lazy-load pages
const Login = () => import('@/pages/auth/Login').then((m) => ({ Component: m.default }));
const Register = () => import('@/pages/auth/Register').then((m) => ({ Component: m.default }));
const ForgotPassword = () =>
  import('@/pages/auth/ForgotPassword').then((m) => ({ Component: m.default }));
const ResetPassword = () =>
  import('@/pages/auth/ResetPassword').then((m) => ({ Component: m.default }));
const VerifyEmail = () =>
  import('@/pages/auth/VerifyEmail').then((m) => ({ Component: m.default }));

const AdminLoginPage = () =>
  import('@/pages/admin/auth/Login').then((m) => ({ Component: m.default }));
const TwoFactorChallenge = () =>
  import('@/pages/auth/TwoFactorChallenge').then((m) => ({ Component: m.default }));
const GoogleReturn = () =>
  import('@/pages/auth/GoogleReturn').then((m) => ({ Component: m.default }));

const CreateShop = () =>
  import('@/pages/onboarding/CreateShop').then((m) => ({ Component: m.default }));
const Dashboard = () => import('@/pages/Dashboard').then((m) => ({ Component: m.default }));
const ProductsIndex = () =>
  import('@/pages/products/Index').then((m) => ({ Component: m.default }));
const ProductsCreate = () =>
  import('@/pages/products/Create').then((m) => ({ Component: m.default }));
const ProductsShow = () => import('@/pages/products/Show').then((m) => ({ Component: m.default }));
const ProductsEdit = () => import('@/pages/products/Edit').then((m) => ({ Component: m.default }));
const ProductsImport = () =>
  import('@/pages/products/Import').then((m) => ({ Component: m.default }));
const ProductsAssistant = () =>
  import('@/pages/products/Assistant').then((m) => ({ Component: m.default }));
const ProductsQuickAdd = () =>
  import('@/pages/products/QuickAdd').then((m) => ({ Component: m.default }));
const ProductsIngredients = () =>
  import('@/pages/owner/bar/ingredients/IngredientsListPage').then((m) => ({
    Component: m.default,
  }));
const ProductsIngredientStock = () =>
  import('@/pages/owner/bar/ingredients/IngredientStockPage').then((m) => ({
    Component: m.default,
  }));
const ProductsIngredientMovements = () =>
  import('@/pages/owner/bar/ingredients/IngredientMovementsPage').then((m) => ({
    Component: m.default,
  }));
const ProductsIngredientInventory = () =>
  import('@/pages/owner/bar/stocktake/StocktakePage').then((m) => ({
    Component: m.default,
  }));
const InventoryStocktake = () =>
  import('@/pages/inventory/Stocktake').then((m) => ({ Component: m.default }));
const InventoryHistory = () =>
  import('@/pages/inventory/StocktakeHistory').then((m) => ({ Component: m.default }));
const InventorySessionDetail = () =>
  import('@/pages/inventory/StocktakeSessionDetail').then((m) => ({ Component: m.default }));
const SalesIndex = () => import('@/pages/sales/Index').then((m) => ({ Component: m.default }));
const SalesShow = () => import('@/pages/sales/Show').then((m) => ({ Component: m.default }));
const SalesStatistics = () =>
  import('@/pages/sales/Statistics').then((m) => ({ Component: m.default }));
const SaleEditApprovals = () =>
  import('@/pages/sales/SaleEditApprovals').then((m) => ({ Component: m.default }));
const StockIndex = () => import('@/pages/stock/Index').then((m) => ({ Component: m.default }));
const StockMovements = () =>
  import('@/pages/stock/Movements').then((m) => ({ Component: m.default }));
const StockTransfers = () =>
  import('@/pages/stock/Transfers').then((m) => ({ Component: m.default }));
const CustomersIndex = () =>
  import('@/pages/customers/Index').then((m) => ({ Component: m.default }));
const CustomersShow = () =>
  import('@/pages/customers/Show').then((m) => ({ Component: m.default }));
const EmployeesIndex = () =>
  import('@/pages/employees/Team').then((m) => ({ Component: m.default }));
const PayrollOperations = () =>
  import('@/pages/payroll/Operations').then((m) => ({ Component: m.default }));
const PayrollIndex = () => import('@/pages/payroll/Index').then((m) => ({ Component: m.default }));
const AccountingIndex = () =>
  import('@/pages/accounting/Index').then((m) => ({ Component: m.default }));
const AccountingInvitationAccept = () =>
  import('@/pages/accounting/InvitationAccept').then((m) => ({ Component: m.default }));
const EmployeesPermissions = () =>
  import('@/pages/employees/Permissions').then((m) => ({ Component: m.default }));
const DriversIndex = () => import('@/pages/drivers/Index').then((m) => ({ Component: m.default }));
const DriversShow = () => import('@/pages/drivers/Show').then((m) => ({ Component: m.default }));
const DeliveriesIndex = () =>
  import('@/pages/deliveries/Index').then((m) => ({ Component: m.default }));
const BrandsIndex = () => import('@/pages/brands/Index').then((m) => ({ Component: m.default }));
const CategoriesIndex = () =>
  import('@/pages/categories/Index').then((m) => ({ Component: m.default }));
const SuppliersIndex = () =>
  import('@/pages/suppliers/Index').then((m) => ({ Component: m.default }));
const AttributesIndex = () =>
  import('@/pages/attributes/Index').then((m) => ({ Component: m.default }));
const SubscriptionIndex = () =>
  import('@/pages/subscription/Index').then((m) => ({ Component: m.default }));
const SubscriptionPlans = () =>
  import('@/pages/subscription/Plans').then((m) => ({ Component: m.default }));
const SubscriptionHistory = () =>
  import('@/pages/subscription/PaymentHistory').then((m) => ({ Component: m.default }));
const SubscriptionCheckout = () =>
  import('@/pages/subscription/Checkout').then((m) => ({ Component: m.default }));
const SubscriptionPaymentResult = () =>
  import('@/pages/subscription/PaymentResult').then((m) => ({ Component: m.default }));
const SettingsDevices = () =>
  import('@/pages/settings/Devices').then((m) => ({ Component: m.default }));
const SettingsAiConnections = () =>
  import('@/pages/settings/AiConnections').then((m) => ({ Component: m.default }));
const ShopsIndex = () => import('@/pages/shops/Index').then((m) => ({ Component: m.default }));
const StockAlerts = () => import('@/pages/stock/Alerts').then((m) => ({ Component: m.default }));
const StockWarehouses = () =>
  import('@/pages/stock/Warehouses').then((m) => ({ Component: m.default }));
const SettingsProfile = () =>
  import('@/pages/settings/Profile').then((m) => ({ Component: m.default }));
const SettingsShop = () => import('@/pages/settings/Shop').then((m) => ({ Component: m.default }));
const SettingsPreferences = () =>
  import('@/pages/settings/Preferences').then((m) => ({ Component: m.default }));
const SettingsNotifications = () =>
  import('@/pages/settings/Notifications').then((m) => ({ Component: m.default }));
const MessagingMessages = () =>
  import('@/pages/messaging/MessagingMessages').then((m) => ({ Component: m.default }));
const MessagingCampaigns = () =>
  import('@/pages/messaging/MessagingCampaigns').then((m) => ({ Component: m.default }));
const MessagingCreditsPaymentResult = () =>
  import('@/pages/messaging/MessagingCreditsPaymentResult').then((m) => ({
    Component: m.default,
  }));
const AdminMessagingDashboard = () =>
  import('@/pages/admin/messaging/Dashboard').then((m) => ({ Component: m.default }));
const AdminMessagingBalances = () =>
  import('@/pages/admin/messaging/Balances').then((m) => ({ Component: m.default }));
const SupportIndex = () => import('@/pages/support/Index').then((m) => ({ Component: m.default }));
const SupportCreate = () =>
  import('@/pages/support/Create').then((m) => ({ Component: m.default }));
const SupportShow = () => import('@/pages/support/Show').then((m) => ({ Component: m.default }));
const AiAnalytics = () => import('@/pages/ai/AiAnalytics').then((m) => ({ Component: m.default }));
const AiChat = () => import('@/pages/ai/Chat').then((m) => ({ Component: m.default }));
const AiCredits = () => import('@/pages/ai/AiCredits').then((m) => ({ Component: m.default }));
const AiCreditsPaymentResult = () =>
  import('@/pages/ai/AiCreditsPaymentResult').then((m) => ({ Component: m.default }));

const GuidePage = () => import('@/pages/guide/Index').then((m) => ({ Component: m.default }));
const PosPage = () => import('@/pages/pos/PosPage').then((m) => ({ Component: m.default }));

// Report pages
const ReportsRegisterSessions = () =>
  import('@/pages/reports/RegisterSessions').then((m) => ({ Component: m.default }));
const ReportsCommissions = () =>
  import('@/pages/reports/Commissions').then((m) => ({ Component: m.default }));

// Admin pages
const AdminDashboard = () =>
  import('@/pages/admin/Dashboard').then((m) => ({ Component: m.default }));
const AdminUsersIndex = () =>
  import('@/pages/admin/users/Index').then((m) => ({ Component: m.default }));
const AdminUsersShow = () =>
  import('@/pages/admin/users/Show').then((m) => ({ Component: m.default }));
const AdminShopsIndex = () =>
  import('@/pages/admin/shops/Index').then((m) => ({ Component: m.default }));
const AdminShopsCreate = () =>
  import('@/pages/admin/shops/Create').then((m) => ({ Component: m.default }));
const AdminShopsShow = () =>
  import('@/pages/admin/shops/Show').then((m) => ({ Component: m.default }));
const AdminShopsDashboard = () =>
  import('@/pages/admin/shops/Dashboard').then((m) => ({ Component: m.default }));
const AdminSubscriptionsIndex = () =>
  import('@/pages/admin/subscriptions/Index').then((m) => ({ Component: m.default }));
const AdminSubscriptionsShow = () =>
  import('@/pages/admin/subscriptions/Show').then((m) => ({ Component: m.default }));
const AdminPlansIndex = () =>
  import('@/pages/admin/plans/Index').then((m) => ({ Component: m.default }));
const AdminPlansCreate = () =>
  import('@/pages/admin/plans/Create').then((m) => ({ Component: m.default }));
const AdminPlansEdit = () =>
  import('@/pages/admin/plans/Edit').then((m) => ({ Component: m.default }));
const AdminFinanceIndex = () =>
  import('@/pages/admin/finance/Index').then((m) => ({ Component: m.default }));
const AdminSystemLogs = () =>
  import('@/pages/admin/system/Logs').then((m) => ({ Component: m.default }));
const AdminSystemHealth = () =>
  import('@/pages/admin/system/Health').then((m) => ({ Component: m.default }));
const AdminSync = () => import('@/pages/admin/sync/Index').then((m) => ({ Component: m.default }));
const AdminQuotesIndex = () =>
  import('@/pages/admin/quotes/Index').then((m) => ({ Component: m.default }));
const AdminQuotesShow = () =>
  import('@/pages/admin/quotes/Show').then((m) => ({ Component: m.default }));
const AdminProfile = () => import('@/pages/admin/Profile').then((m) => ({ Component: m.default }));
const AdminAnnouncementsIndex = () =>
  import('@/pages/admin/announcements/Index').then((m) => ({ Component: m.default }));
const AdminAiDocumentStyles = () =>
  import('@/pages/admin/ai/DocumentStyles').then((m) => ({ Component: m.default }));
const AdminAiAnalytics = () =>
  import('@/pages/admin/ai/Analytics').then((m) => ({ Component: m.default }));
const AdminPaymentsIndex = () =>
  import('@/pages/admin/payments/Index').then((m) => ({ Component: m.default }));
const AdminSupportIndex = () =>
  import('@/pages/admin/support/Index').then((m) => ({ Component: m.default }));
const AdminSupportShow = () =>
  import('@/pages/admin/support/Show').then((m) => ({ Component: m.default }));

// Sprint 3 — Promotions
const PromotionsIndex = () =>
  import('@/pages/owner/promotions/PromotionsListPage').then((m) => ({ Component: m.default }));
// Pages commerciales (module Commerce)
const CommercePagesIndex = () =>
  import('@/pages/owner/commerce/CommercePagesIndex').then((m) => ({ Component: m.default }));
const CommercePageCreate = () =>
  import('@/pages/owner/commerce/CommercePageCreate').then((m) => ({ Component: m.default }));
const CommercePageEdit = () =>
  import('@/pages/owner/commerce/CommercePageEdit').then((m) => ({ Component: m.default }));
const CommerceActivationPage = () =>
  import('@/pages/owner/commerce/CommerceActivationPage').then((m) => ({ Component: m.default }));
const CommerceOrdersInbox = () =>
  import('@/pages/owner/commerce/orders/OrdersInbox').then((m) => ({ Component: m.default }));
const CommerceOrderShow = () =>
  import('@/pages/owner/commerce/orders/OrderShow').then((m) => ({ Component: m.default }));
const CommerceReservationsInbox = () =>
  import('@/pages/owner/commerce/reservations/ReservationsInbox').then((m) => ({
    Component: m.default,
  }));
const CommerceReservationShow = () =>
  import('@/pages/owner/commerce/reservations/ReservationShow').then((m) => ({
    Component: m.default,
  }));
const CommerceWithdrawals = () =>
  import('@/pages/owner/commerce/CommerceWithdrawals').then((m) => ({ Component: m.default }));
// Storefront public (shop.velko-pos.com / local /shop/*)
const VelkoBusinessHub = () =>
  import('@/pages/storefront/VelkoBusinessHub').then((m) => ({ Component: m.default }));
const StorePage = () =>
  import('@/pages/storefront/StorePage').then((m) => ({ Component: m.default }));
const StoreProductPage = () =>
  import('@/pages/storefront/StoreProductPage').then((m) => ({ Component: m.default }));
const StoreCartPage = () =>
  import('@/pages/storefront/StoreCartPage').then((m) => ({ Component: m.default }));
const StoreCheckoutPage = () =>
  import('@/pages/storefront/StoreCheckoutPage').then((m) => ({ Component: m.default }));
const StoreConfirmationPage = () =>
  import('@/pages/storefront/StoreConfirmationPage').then((m) => ({ Component: m.default }));
const StorePaymentReturnPage = () =>
  import('@/pages/storefront/StorePaymentReturnPage').then((m) => ({ Component: m.default }));
const StoreOrdersPage = () =>
  import('@/pages/storefront/StoreOrdersPage').then((m) => ({ Component: m.default }));
const StoreOrderTrackingPage = () =>
  import('@/pages/storefront/StoreOrderTrackingPage').then((m) => ({ Component: m.default }));
const StoreReservationPage = () =>
  import('@/pages/storefront/StoreReservationPage').then((m) => ({ Component: m.default }));
const StoreReservationsPage = () =>
  import('@/pages/storefront/StoreReservationsPage').then((m) => ({ Component: m.default }));
const StoreReservationTrackingPage = () =>
  import('@/pages/storefront/StoreReservationTrackingPage').then((m) => ({
    Component: m.default,
  }));
const StoreNotFound = () =>
  import('@/pages/storefront/StoreNotFound').then((m) => ({ Component: m.default }));
// Velko Bar / Contrôle — Phase 2 (front)
const BarIngredientsIndex = () =>
  import('@/pages/owner/bar/ingredients/IngredientsListPage').then((m) => ({
    Component: m.default,
  }));
const BarRecipesIndex = () =>
  import('@/pages/owner/bar/recipes/RecipesListPage').then((m) => ({
    Component: m.default,
  }));
const BarStocktakeIndex = () =>
  import('@/pages/owner/bar/stocktake/StocktakePage').then((m) => ({
    Component: m.default,
  }));
const BarVarianceIndex = () =>
  import('@/pages/owner/bar/variance/VariancePage').then((m) => ({
    Component: m.default,
  }));
const BarConsumptionIndex = () =>
  import('@/pages/owner/bar/consumption/ConsumptionPage').then((m) => ({
    Component: m.default,
  }));
const BarDashboardIndex = () =>
  import('@/pages/owner/bar/dashboard/BarDashboardPage').then((m) => ({
    Component: m.default,
  }));
const BarInvestorIndex = () =>
  import('@/pages/owner/bar/investor/InvestorDashboardPage').then((m) => ({
    Component: m.default,
  }));
const RentalIndex = () =>
  import('@/pages/owner/rental/RentalWorkspace').then((m) => ({ Component: m.default }));
// Module Partenaire (consignation) — Phase 2 (front)
const PartnersIndex = () =>
  import('@/pages/owner/partners/PartnersListPage').then((m) => ({ Component: m.default }));
const PartnersConsignmentsIndex = () =>
  import('@/pages/owner/partners/consignments/ConsignmentsPage').then((m) => ({
    Component: m.default,
  }));
const PartnersSettlementsIndex = () =>
  import('@/pages/owner/partners/settlements/SettlementsPage').then((m) => ({
    Component: m.default,
  }));
const PartnerDetailIndex = () =>
  import('@/pages/owner/partners/detail/PartnerDetailPage').then((m) => ({ Component: m.default }));

// Returns module
const ReturnsIndex = () => import('@/pages/returns/Index').then((m) => ({ Component: m.default }));
const ReturnsShow = () => import('@/pages/returns/Show').then((m) => ({ Component: m.default }));
const ReturnsNew = () => import('@/pages/returns/New').then((m) => ({ Component: m.default }));
const ReturnsCreate = () =>
  import('@/pages/returns/Create').then((m) => ({ Component: m.default }));
const ReturnsExchange = () =>
  import('@/pages/returns/Exchange').then((m) => ({ Component: m.default }));
const ReturnsApprovals = () =>
  import('@/pages/returns/Approvals').then((m) => ({ Component: m.default }));

// Marketing pages
const MarketingHome = () =>
  import('@/pages/marketing/Home').then((m) => ({ Component: m.default }));
const MarketingFeatures = () =>
  import('@/pages/marketing/Features').then((m) => ({ Component: m.default }));
const MarketingPricing = () =>
  import('@/pages/marketing/Pricing').then((m) => ({ Component: m.default }));
const MarketingAbout = () =>
  import('@/pages/marketing/About').then((m) => ({ Component: m.default }));
const MarketingContact = () =>
  import('@/pages/marketing/Contact').then((m) => ({ Component: m.default }));
const MarketingDemo = () =>
  import('@/pages/marketing/Demo').then((m) => ({ Component: m.default }));
const MarketingDownload = () =>
  import('@/pages/marketing/Download').then((m) => ({ Component: m.default }));
const MarketingInstallGuide = () =>
  import('@/pages/marketing/InstallGuide').then((m) => ({ Component: m.default }));
const MarketingBar = () => import('@/pages/marketing/Bar').then((m) => ({ Component: m.default }));
const MarketingTroc = () =>
  import('@/pages/marketing/Troc').then((m) => ({ Component: m.default }));
const MarketingGestionRetours = () =>
  import('@/pages/marketing/GestionRetours').then((m) => ({ Component: m.default }));
const MarketingPartenaires = () =>
  import('@/pages/marketing/Partenaires').then((m) => ({ Component: m.default }));
const MarketingCompareCahier = () =>
  import('@/pages/marketing/CompareCahier').then((m) => ({ Component: m.default }));
const MarketingLegal = () =>
  import('@/pages/marketing/Legal').then((m) => ({ Component: m.default }));
const MarketingPrivacy = () =>
  import('@/pages/marketing/Privacy').then((m) => ({ Component: m.default }));
const MarketingTerms = () =>
  import('@/pages/marketing/Terms').then((m) => ({ Component: m.default }));
const MarketingDataDeletion = () =>
  import('@/pages/marketing/DataDeletion').then((m) => ({ Component: m.default }));
const MarketingBlog = () =>
  import('@/pages/marketing/Blog').then((m) => ({ Component: m.default }));
const MarketingBlogPost = () =>
  import('@/pages/marketing/BlogPost').then((m) => ({ Component: m.default }));

import CockpitLayout from '@/layouts/CockpitLayout';
import PosLayout from '@/layouts/PosLayout';
import { PosGuard } from '@/guards/PosGuard';
import AdminLayout from '@/layouts/AdminLayout';
import AdminAuthLayoutV2 from '@/layouts/AdminAuthLayoutV2';
import MarketingLayoutV2 from '@/layouts/MarketingLayoutV2';
import StorefrontLayout from '@/layouts/StorefrontLayout';
import AppRouteErrorBoundary from '@/components/errors/AppRouteErrorBoundary';

// =============================================================================
// MARKETING tree (velko-pos.com)
// =============================================================================
// Routes: marketing pages + public auth pages (login, register, etc.)
// Owner/admin routes are NOT defined here — nginx 301-strips /owner/* and
// /admin/* to the app and admin hosts respectively.
const marketingRoutes: RouteObject[] = [
  {
    errorElement: <AppRouteErrorBoundary />,
    element: <MarketingLayoutV2 />,
    children: [
      { path: '/', lazy: MarketingHome },
      { path: '/fonctionnalites', lazy: MarketingFeatures },
      { path: '/tarifs', lazy: MarketingPricing },
      { path: '/blog', lazy: MarketingBlog },
      { path: '/blog/:slug', lazy: MarketingBlogPost },
      { path: '/a-propos', lazy: MarketingAbout },
      { path: '/contact', lazy: MarketingContact },
      { path: '/demo', lazy: MarketingDemo },
      { path: '/telechargement', lazy: MarketingDownload },
      { path: '/guide-installation', lazy: MarketingInstallGuide },
      { path: '/velko-bar', lazy: MarketingBar },
      { path: '/troc', lazy: MarketingTroc },
      { path: '/gestion-retours', lazy: MarketingGestionRetours },
      { path: '/partenaires', lazy: MarketingPartenaires },
      { path: '/velko-vs-cahier-de-caisse', lazy: MarketingCompareCahier },
      { path: '/mentions-legales', lazy: MarketingLegal },
      { path: '/politique-confidentialite', lazy: MarketingPrivacy },
      { path: '/conditions-utilisation', lazy: MarketingTerms },
      { path: '/suppression-donnees', lazy: MarketingDataDeletion },
    ],
  },
  // Auth pages (owner login, register). Cookie session is shared cross-subdomain
  // via SESSION_DOMAIN=.velko-pos.com, so after login the redirect to app host
  // is recognized as authenticated (no loop).
  {
    errorElement: <AppRouteErrorBoundary />,
    element: <GuestGuard />,
    children: [
      { path: '/login', lazy: Login },
      { path: '/two-factor-challenge', lazy: TwoFactorChallenge },
      { path: '/register', lazy: Register },
      { path: '/forgot-password', lazy: ForgotPassword },
      { path: '/reset-password/:token', lazy: ResetPassword },
    ],
  },
  // OAuth callback — hors GuestGuard : doit être accessible même si l'état
  // d'auth est ambigu (le token arrive justement via cette route).
  {
    errorElement: <AppRouteErrorBoundary />,
    children: [{ path: '/auth/google/return', lazy: GoogleReturn }],
  },
  // Default redirect for any unknown path (auth-aware routing to the right host).
  { path: '*', element: <DefaultRedirect />, errorElement: <AppRouteErrorBoundary /> },
];

// =============================================================================
// APP tree (app.velko-pos.com)
// =============================================================================
// Owner routes WITHOUT the legacy /owner/ prefix. `app.velko-pos.com/` itself
// renders Dashboard via the index route (when authenticated).
const appRoutes: RouteObject[] = [
  // Legacy /owner/* redirect for in-host navigation (e.g. /owner/inventory/stocktake -> /inventory/stocktake)
  { path: '/owner', element: <LegacyOwnerRedirect /> },
  { path: '/owner/*', element: <LegacyOwnerRedirect /> },
  {
    errorElement: <AppRouteErrorBoundary />,
    element: <AuthGuard />,
    children: [
      { path: '/onboarding', lazy: CreateShop },
      { path: '/verify-email', lazy: VerifyEmail },
      { path: '/finances/invitations/accept', lazy: AccountingInvitationAccept },
      {
        element: <PosLayout />,
        children: [{ element: <PosGuard />, children: [{ path: '/pos', lazy: PosPage }] }],
      },
      {
        element: <CockpitLayout />,
        children: [
          { index: true, lazy: Dashboard },
          { path: '/dashboard', lazy: Dashboard },
          { path: '/products', lazy: ProductsIndex },
          { path: '/products/create', lazy: ProductsCreate, handle: { shopSwitchTo: '/products' } },
          {
            path: '/products/assistant',
            lazy: ProductsAssistant,
            handle: { shopSwitchTo: '/products' },
          },
          {
            path: '/products/quick-add',
            lazy: ProductsQuickAdd,
            handle: { shopSwitchTo: '/products' },
          },
          { path: '/products/import', lazy: ProductsImport, handle: { shopSwitchTo: '/products' } },
          { path: '/products/ingredients', lazy: ProductsIngredients },
          { path: '/products/ingredients/stock', lazy: ProductsIngredientStock },
          { path: '/products/ingredients/movements', lazy: ProductsIngredientMovements },
          { path: '/products/ingredients/inventory', lazy: ProductsIngredientInventory },
          { path: '/inventory/stocktake', lazy: InventoryStocktake },
          { path: '/inventory/history', lazy: InventoryHistory },
          {
            path: '/inventory/history/:sessionId',
            lazy: InventorySessionDetail,
            handle: { shopSwitchTo: '/inventory/history' },
          },
          { path: '/products/:id', lazy: ProductsShow, handle: { shopSwitchTo: '/products' } },
          { path: '/products/:id/edit', lazy: ProductsEdit, handle: { shopSwitchTo: '/products' } },
          { path: '/sales', lazy: SalesIndex },
          { path: '/sales/statistics', lazy: SalesStatistics },
          { path: '/sales/approvals', lazy: SaleEditApprovals },
          { path: '/sales/:id', lazy: SalesShow, handle: { shopSwitchTo: '/sales' } },
          { path: '/stock', lazy: StockIndex },
          { path: '/stock/movements', lazy: StockMovements },
          { path: '/stock/transfers', lazy: StockTransfers },
          { path: '/stock/alerts', lazy: StockAlerts },
          { path: '/stock/warehouses', lazy: StockWarehouses },
          { path: '/customers', lazy: CustomersIndex },
          { path: '/customers/:id', lazy: CustomersShow, handle: { shopSwitchTo: '/customers' } },
          { path: '/employees', lazy: EmployeesIndex },
          { path: '/employees/permissions', lazy: EmployeesPermissions },
          { path: '/payroll', lazy: PayrollIndex },
          { path: '/payroll/operations', lazy: PayrollOperations },
          { path: '/finances', lazy: AccountingIndex },
          { path: '/finances/achats', lazy: AccountingIndex },
          { path: '/finances/charges-recurrentes', lazy: AccountingIndex },
          { path: '/finances/a-payer', lazy: AccountingIndex },
          { path: '/finances/sessions', lazy: ReportsRegisterSessions },
          { path: '/finances/rapprochement', lazy: AccountingIndex },
          { path: '/finances/journal', lazy: AccountingIndex },
          { path: '/finances/exports', lazy: AccountingIndex },
          { path: '/finances/reglages', lazy: AccountingIndex },
          { path: '/drivers', lazy: DriversIndex },
          { path: '/drivers/:id', lazy: DriversShow, handle: { shopSwitchTo: '/drivers' } },
          { path: '/deliveries', lazy: DeliveriesIndex },
          { path: '/brands', lazy: BrandsIndex },
          { path: '/categories', lazy: CategoriesIndex },
          { path: '/suppliers', lazy: SuppliersIndex },
          { path: '/attributes', lazy: AttributesIndex },
          { path: '/subscription', lazy: SubscriptionIndex },
          { path: '/subscription/plans', lazy: SubscriptionPlans },
          { path: '/subscription/history', lazy: SubscriptionHistory },
          {
            path: '/subscription/checkout',
            lazy: SubscriptionCheckout,
            handle: { shopSwitchTo: '/subscription/plans' },
          },
          {
            path: '/subscription/payment-result',
            lazy: SubscriptionPaymentResult,
            handle: { shopSwitchTo: '/subscription' },
          },
          { path: '/settings', element: <Navigate to="/settings/profile" replace /> },
          { path: '/settings/devices', lazy: SettingsDevices },
          { path: '/settings/ai-connections', lazy: SettingsAiConnections },
          { path: '/settings/profile', lazy: SettingsProfile },
          { path: '/settings/shop', lazy: SettingsShop },
          { path: '/settings/preferences', lazy: SettingsPreferences },
          { path: '/settings/notifications', lazy: SettingsNotifications },
          { path: '/messaging/messages', lazy: MessagingMessages },
          { path: '/messaging/campaigns', lazy: MessagingCampaigns },
          {
            path: '/messaging/credits/payment-result',
            lazy: MessagingCreditsPaymentResult,
            handle: { shopSwitchTo: '/messaging/messages' },
          },
          { path: '/reports/commissions', lazy: ReportsCommissions },
          { path: '/reports', element: <Navigate to="/finances" replace /> },
          { path: '/reports/sales', element: <Navigate to="/finances" replace /> },
          { path: '/reports/profitability', element: <Navigate to="/finances" replace /> },
          { path: '/reports/employees', element: <Navigate to="/finances" replace /> },
          { path: '/reports/credits', element: <Navigate to="/finances" replace /> },
          { path: '/reports/treasury', element: <Navigate to="/finances" replace /> },
          { path: '/reports/sessions', element: <Navigate to="/finances/sessions" replace /> },
          { path: '/shops', lazy: ShopsIndex },
          { path: '/support', lazy: SupportIndex },
          { path: '/support/create', lazy: SupportCreate, handle: { shopSwitchTo: '/support' } },
          { path: '/support/:id', lazy: SupportShow, handle: { shopSwitchTo: '/support' } },
          { path: '/ai', lazy: AiAnalytics },
          { path: '/ai/chat', lazy: AiChat },
          { path: '/ai/credits', lazy: AiCredits },
          {
            path: '/ai/credits/payment-result',
            lazy: AiCreditsPaymentResult,
            handle: { shopSwitchTo: '/ai/credits' },
          },
          { path: '/guide', lazy: GuidePage },
          { path: '/promotions', lazy: PromotionsIndex },
          // Pages commerciales
          { path: '/commerce', lazy: CommercePagesIndex },
          { path: '/commerce/activate', lazy: CommerceActivationPage },
          {
            path: '/commerce/pages/new',
            lazy: CommercePageCreate,
            handle: { shopSwitchTo: '/commerce' },
          },
          {
            path: '/commerce/pages/:id',
            lazy: CommercePageEdit,
            handle: { shopSwitchTo: '/commerce' },
          },
          { path: '/commerce/orders', lazy: CommerceOrdersInbox },
          {
            path: '/commerce/orders/:id',
            lazy: CommerceOrderShow,
            handle: { shopSwitchTo: '/commerce/orders' },
          },
          { path: '/commerce/reservations', lazy: CommerceReservationsInbox },
          {
            path: '/commerce/reservations/:id',
            lazy: CommerceReservationShow,
            handle: { shopSwitchTo: '/commerce/reservations' },
          },
          { path: '/commerce/withdrawals', lazy: CommerceWithdrawals },
          // Velko Bar / Contrôle
          { path: '/bar/ingredients', lazy: BarIngredientsIndex },
          { path: '/bar/recipes', lazy: BarRecipesIndex },
          { path: '/bar/stocktake', lazy: BarStocktakeIndex },
          { path: '/bar/variance', lazy: BarVarianceIndex },
          { path: '/bar/consumption', lazy: BarConsumptionIndex },
          { path: '/bar/dashboard', lazy: BarDashboardIndex },
          { path: '/bar/investor', lazy: BarInvestorIndex },
          { path: '/rental', lazy: RentalIndex },
          // Module Partenaire (consignation)
          { path: '/partners', lazy: PartnersIndex },
          { path: '/partners/consignments', lazy: PartnersConsignmentsIndex },
          { path: '/partners/settlements', lazy: PartnersSettlementsIndex },
          {
            path: '/partners/:id',
            lazy: PartnerDetailIndex,
            handle: { shopSwitchTo: '/partners' },
          },
          // Returns module
          { path: '/returns', lazy: ReturnsIndex },
          { path: '/returns/approvals', lazy: ReturnsApprovals },
          { path: '/returns/new', lazy: ReturnsNew, handle: { shopSwitchTo: '/returns' } },
          { path: '/returns/refund', lazy: ReturnsCreate, handle: { shopSwitchTo: '/returns' } },
          {
            path: '/returns/exchange',
            lazy: ReturnsExchange,
            handle: { shopSwitchTo: '/returns' },
          },
          { path: '/returns/:id', lazy: ReturnsShow, handle: { shopSwitchTo: '/returns' } },
        ],
      },
    ],
  },
  // OAuth callback — hors AuthGuard : le token arrive justement via cette
  // route, donc l'app host doit la servir. Sans elle le catch-all renvoie
  // vers le marketing /login et le token est perdu.
  {
    errorElement: <AppRouteErrorBoundary />,
    children: [{ path: '/auth/google/return', lazy: GoogleReturn }],
  },
  // Catch-all: cross-host to marketing /login (typically when AuthGuard fails)
  {
    path: '*',
    element: <CrossHostNavigate to="/login" replace />,
    errorElement: <AppRouteErrorBoundary />,
  },
];

// =============================================================================
// ADMIN tree (admin.velko-pos.com)
// =============================================================================
// Admin routes WITHOUT the legacy /admin/ prefix. `admin.velko-pos.com/` renders
// AdminDashboard via the index route.
const adminRoutes: RouteObject[] = [
  // Legacy /admin/* redirect for in-host navigation (e.g. /admin/users -> /users)
  { path: '/admin', element: <LegacyAdminRedirect /> },
  { path: '/admin/*', element: <LegacyAdminRedirect /> },
  // Guest admin (login page)
  {
    errorElement: <AppRouteErrorBoundary />,
    element: <AdminGuestGuard />,
    children: [
      {
        element: <AdminAuthLayoutV2 />,
        children: [{ path: '/login', lazy: AdminLoginPage }],
      },
    ],
  },
  // Authenticated admin
  {
    errorElement: <AppRouteErrorBoundary />,
    element: <AdminGuard />,
    children: [
      {
        element: <AdminLayout />,
        children: [
          { index: true, lazy: AdminDashboard },
          { path: '/users', lazy: AdminUsersIndex },
          { path: '/users/:id', lazy: AdminUsersShow },
          { path: '/shops', lazy: AdminShopsIndex },
          { path: '/shops/create', lazy: AdminShopsCreate },
          { path: '/shops/:id', lazy: AdminShopsShow },
          { path: '/shops/:id/dashboard', lazy: AdminShopsDashboard },
          { path: '/subscriptions', lazy: AdminSubscriptionsIndex },
          { path: '/subscriptions/:id', lazy: AdminSubscriptionsShow },
          { path: '/plans', lazy: AdminPlansIndex },
          { path: '/plans/create', lazy: AdminPlansCreate },
          { path: '/plans/:id/edit', lazy: AdminPlansEdit },
          { path: '/payments', lazy: AdminPaymentsIndex },
          { path: '/finance', lazy: AdminFinanceIndex },
          { path: '/system/logs', lazy: AdminSystemLogs },
          { path: '/system/health', lazy: AdminSystemHealth },
          { path: '/sync', lazy: AdminSync },
          { path: '/quotes', lazy: AdminQuotesIndex },
          { path: '/quotes/:id', lazy: AdminQuotesShow },
          { path: '/announcements', lazy: AdminAnnouncementsIndex },
          { path: '/ai/document-styles', lazy: AdminAiDocumentStyles },
          { path: '/ai/analytics', lazy: AdminAiAnalytics },
          { path: '/support', lazy: AdminSupportIndex },
          { path: '/support/:id', lazy: AdminSupportShow },
          { path: '/profile', lazy: AdminProfile },
        ],
      },
    ],
  },
  // Catch-all: redirect to admin login (in-host)
  {
    path: '*',
    element: <Navigate to="/login" replace />,
    errorElement: <AppRouteErrorBoundary />,
  },
];

// =============================================================================
// LOCAL tree (localhost / 127.0.0.1 / *.local)
// =============================================================================
// A single dev server serves every role at once. Owner routes are prefixed with
// /owner/, admin routes with /admin/, marketing + auth at root. This mirrors the
// monolithic pre-multi-host layout so dev workflows keep working without DNS.
const localRoutes: RouteObject[] = [
  // Marketing pages
  {
    errorElement: <AppRouteErrorBoundary />,
    element: <MarketingLayoutV2 />,
    children: [
      { path: '/', lazy: MarketingHome },
      { path: '/fonctionnalites', lazy: MarketingFeatures },
      { path: '/tarifs', lazy: MarketingPricing },
      { path: '/blog', lazy: MarketingBlog },
      { path: '/blog/:slug', lazy: MarketingBlogPost },
      { path: '/a-propos', lazy: MarketingAbout },
      { path: '/contact', lazy: MarketingContact },
      { path: '/demo', lazy: MarketingDemo },
      { path: '/telechargement', lazy: MarketingDownload },
      { path: '/guide-installation', lazy: MarketingInstallGuide },
      { path: '/velko-bar', lazy: MarketingBar },
      { path: '/troc', lazy: MarketingTroc },
      { path: '/gestion-retours', lazy: MarketingGestionRetours },
      { path: '/partenaires', lazy: MarketingPartenaires },
      { path: '/velko-vs-cahier-de-caisse', lazy: MarketingCompareCahier },
      { path: '/mentions-legales', lazy: MarketingLegal },
      { path: '/politique-confidentialite', lazy: MarketingPrivacy },
      { path: '/conditions-utilisation', lazy: MarketingTerms },
      { path: '/suppression-donnees', lazy: MarketingDataDeletion },
    ],
  },
  // Owner guest (login/register at root)
  {
    errorElement: <AppRouteErrorBoundary />,
    element: <GuestGuard />,
    children: [
      { path: '/login', lazy: Login },
      { path: '/two-factor-challenge', lazy: TwoFactorChallenge },
      { path: '/register', lazy: Register },
      { path: '/forgot-password', lazy: ForgotPassword },
      { path: '/reset-password/:token', lazy: ResetPassword },
    ],
  },
  // OAuth callback — hors GuestGuard (token arrive via cette route)
  {
    errorElement: <AppRouteErrorBoundary />,
    children: [{ path: '/auth/google/return', lazy: GoogleReturn }],
  },
  // Admin guest (login at /admin/login)
  {
    errorElement: <AppRouteErrorBoundary />,
    element: <AdminGuestGuard />,
    children: [
      {
        element: <AdminAuthLayoutV2 />,
        children: [{ path: '/admin/login', lazy: AdminLoginPage }],
      },
    ],
  },
  // Owner authenticated (prefixed /owner/)
  {
    errorElement: <AppRouteErrorBoundary />,
    element: <AuthGuard />,
    children: [
      { path: '/owner/onboarding', lazy: CreateShop },
      { path: '/owner/verify-email', lazy: VerifyEmail },
      { path: '/owner/finances/invitations/accept', lazy: AccountingInvitationAccept },
      {
        element: <PosLayout />,
        children: [{ element: <PosGuard />, children: [{ path: '/pos', lazy: PosPage }] }],
      },
      {
        element: <CockpitLayout />,
        children: [
          { path: '/owner/dashboard', lazy: Dashboard },
          { path: '/owner/products', lazy: ProductsIndex },
          {
            path: '/owner/products/create',
            lazy: ProductsCreate,
            handle: { shopSwitchTo: '/products' },
          },
          {
            path: '/owner/products/assistant',
            lazy: ProductsAssistant,
            handle: { shopSwitchTo: '/products' },
          },
          {
            path: '/owner/products/quick-add',
            lazy: ProductsQuickAdd,
            handle: { shopSwitchTo: '/products' },
          },
          {
            path: '/owner/products/import',
            lazy: ProductsImport,
            handle: { shopSwitchTo: '/products' },
          },
          { path: '/owner/products/ingredients', lazy: ProductsIngredients },
          { path: '/owner/products/ingredients/stock', lazy: ProductsIngredientStock },
          { path: '/owner/products/ingredients/movements', lazy: ProductsIngredientMovements },
          { path: '/owner/products/ingredients/inventory', lazy: ProductsIngredientInventory },
          { path: '/owner/inventory/stocktake', lazy: InventoryStocktake },
          { path: '/owner/inventory/history', lazy: InventoryHistory },
          {
            path: '/owner/inventory/history/:sessionId',
            lazy: InventorySessionDetail,
            handle: { shopSwitchTo: '/inventory/history' },
          },
          {
            path: '/owner/products/:id',
            lazy: ProductsShow,
            handle: { shopSwitchTo: '/products' },
          },
          {
            path: '/owner/products/:id/edit',
            lazy: ProductsEdit,
            handle: { shopSwitchTo: '/products' },
          },
          { path: '/owner/sales', lazy: SalesIndex },
          { path: '/owner/sales/statistics', lazy: SalesStatistics },
          { path: '/owner/sales/approvals', lazy: SaleEditApprovals },
          { path: '/owner/sales/:id', lazy: SalesShow, handle: { shopSwitchTo: '/sales' } },
          { path: '/owner/stock', lazy: StockIndex },
          { path: '/owner/stock/movements', lazy: StockMovements },
          { path: '/owner/stock/transfers', lazy: StockTransfers },
          { path: '/owner/stock/alerts', lazy: StockAlerts },
          { path: '/owner/stock/warehouses', lazy: StockWarehouses },
          { path: '/owner/customers', lazy: CustomersIndex },
          {
            path: '/owner/customers/:id',
            lazy: CustomersShow,
            handle: { shopSwitchTo: '/customers' },
          },
          { path: '/owner/employees', lazy: EmployeesIndex },
          { path: '/owner/employees/permissions', lazy: EmployeesPermissions },
          { path: '/owner/payroll', lazy: PayrollIndex },
          { path: '/owner/payroll/operations', lazy: PayrollOperations },
          { path: '/owner/finances', lazy: AccountingIndex },
          { path: '/owner/finances/achats', lazy: AccountingIndex },
          { path: '/owner/finances/charges-recurrentes', lazy: AccountingIndex },
          { path: '/owner/finances/a-payer', lazy: AccountingIndex },
          { path: '/owner/finances/sessions', lazy: ReportsRegisterSessions },
          { path: '/owner/finances/rapprochement', lazy: AccountingIndex },
          { path: '/owner/finances/journal', lazy: AccountingIndex },
          { path: '/owner/finances/exports', lazy: AccountingIndex },
          { path: '/owner/finances/reglages', lazy: AccountingIndex },
          { path: '/owner/drivers', lazy: DriversIndex },
          { path: '/owner/drivers/:id', lazy: DriversShow, handle: { shopSwitchTo: '/drivers' } },
          { path: '/owner/deliveries', lazy: DeliveriesIndex },
          { path: '/owner/brands', lazy: BrandsIndex },
          { path: '/owner/categories', lazy: CategoriesIndex },
          { path: '/owner/suppliers', lazy: SuppliersIndex },
          { path: '/owner/attributes', lazy: AttributesIndex },
          { path: '/owner/subscription', lazy: SubscriptionIndex },
          { path: '/owner/subscription/plans', lazy: SubscriptionPlans },
          { path: '/owner/subscription/history', lazy: SubscriptionHistory },
          {
            path: '/owner/subscription/checkout',
            lazy: SubscriptionCheckout,
            handle: { shopSwitchTo: '/subscription/plans' },
          },
          {
            path: '/owner/subscription/payment-result',
            lazy: SubscriptionPaymentResult,
            handle: { shopSwitchTo: '/subscription' },
          },
          { path: '/owner/settings', element: <Navigate to="/owner/settings/profile" replace /> },
          { path: '/owner/settings/devices', lazy: SettingsDevices },
          { path: '/owner/settings/ai-connections', lazy: SettingsAiConnections },
          { path: '/owner/settings/profile', lazy: SettingsProfile },
          { path: '/owner/settings/shop', lazy: SettingsShop },
          { path: '/owner/settings/preferences', lazy: SettingsPreferences },
          { path: '/owner/settings/notifications', lazy: SettingsNotifications },
          { path: '/owner/messaging/messages', lazy: MessagingMessages },
          { path: '/owner/messaging/campaigns', lazy: MessagingCampaigns },
          {
            path: '/owner/messaging/credits/payment-result',
            lazy: MessagingCreditsPaymentResult,
          },
          { path: '/owner/reports/commissions', lazy: ReportsCommissions },
          { path: '/owner/reports', element: <Navigate to="/owner/finances" replace /> },
          { path: '/owner/reports/sales', element: <Navigate to="/owner/finances" replace /> },
          {
            path: '/owner/reports/profitability',
            element: <Navigate to="/owner/finances" replace />,
          },
          { path: '/owner/reports/employees', element: <Navigate to="/owner/finances" replace /> },
          { path: '/owner/reports/credits', element: <Navigate to="/owner/finances" replace /> },
          { path: '/owner/reports/treasury', element: <Navigate to="/owner/finances" replace /> },
          {
            path: '/owner/reports/sessions',
            element: <Navigate to="/owner/finances/sessions" replace />,
          },
          { path: '/owner/shops', lazy: ShopsIndex },
          { path: '/owner/support', lazy: SupportIndex },
          {
            path: '/owner/support/create',
            lazy: SupportCreate,
            handle: { shopSwitchTo: '/support' },
          },
          { path: '/owner/support/:id', lazy: SupportShow, handle: { shopSwitchTo: '/support' } },
          { path: '/owner/ai', lazy: AiAnalytics },
          { path: '/owner/ai/chat', lazy: AiChat },
          { path: '/owner/ai/credits', lazy: AiCredits },
          {
            path: '/owner/ai/credits/payment-result',
            lazy: AiCreditsPaymentResult,
            handle: { shopSwitchTo: '/ai/credits' },
          },
          { path: '/owner/guide', lazy: GuidePage },
          { path: '/owner/promotions', lazy: PromotionsIndex },
          { path: '/owner/commerce', lazy: CommercePagesIndex },
          { path: '/owner/commerce/activate', lazy: CommerceActivationPage },
          {
            path: '/owner/commerce/pages/new',
            lazy: CommercePageCreate,
            handle: { shopSwitchTo: '/commerce' },
          },
          {
            path: '/owner/commerce/pages/:id',
            lazy: CommercePageEdit,
            handle: { shopSwitchTo: '/commerce' },
          },
          { path: '/owner/commerce/orders', lazy: CommerceOrdersInbox },
          {
            path: '/owner/commerce/orders/:id',
            lazy: CommerceOrderShow,
            handle: { shopSwitchTo: '/commerce/orders' },
          },
          { path: '/owner/commerce/reservations', lazy: CommerceReservationsInbox },
          {
            path: '/owner/commerce/reservations/:id',
            lazy: CommerceReservationShow,
            handle: { shopSwitchTo: '/commerce/reservations' },
          },
          { path: '/owner/commerce/withdrawals', lazy: CommerceWithdrawals },
          // Velko Bar / Contrôle
          { path: '/owner/bar/ingredients', lazy: BarIngredientsIndex },
          { path: '/owner/bar/recipes', lazy: BarRecipesIndex },
          { path: '/owner/bar/stocktake', lazy: BarStocktakeIndex },
          { path: '/owner/bar/variance', lazy: BarVarianceIndex },
          { path: '/owner/bar/consumption', lazy: BarConsumptionIndex },
          { path: '/owner/bar/dashboard', lazy: BarDashboardIndex },
          { path: '/owner/bar/investor', lazy: BarInvestorIndex },
          { path: '/owner/rental', lazy: RentalIndex },
          // Module Partenaire (consignation) — also exposed without /owner/ prefix above
          { path: '/owner/partners', lazy: PartnersIndex },
          { path: '/owner/partners/consignments', lazy: PartnersConsignmentsIndex },
          { path: '/owner/partners/settlements', lazy: PartnersSettlementsIndex },
          {
            path: '/owner/partners/:id',
            lazy: PartnerDetailIndex,
            handle: { shopSwitchTo: '/partners' },
          },
          // Returns module (also exposed in the prod host without /owner/ prefix above)
          { path: '/owner/returns', lazy: ReturnsIndex },
          { path: '/owner/returns/approvals', lazy: ReturnsApprovals },
          { path: '/owner/returns/new', lazy: ReturnsNew, handle: { shopSwitchTo: '/returns' } },
          {
            path: '/owner/returns/refund',
            lazy: ReturnsCreate,
            handle: { shopSwitchTo: '/returns' },
          },
          {
            path: '/owner/returns/exchange',
            lazy: ReturnsExchange,
            handle: { shopSwitchTo: '/returns' },
          },
          { path: '/owner/returns/:id', lazy: ReturnsShow, handle: { shopSwitchTo: '/returns' } },
        ],
      },
    ],
  },
  // Admin authenticated (prefixed /admin/)
  {
    errorElement: <AppRouteErrorBoundary />,
    element: <AdminGuard />,
    children: [
      {
        element: <AdminLayout />,
        children: [
          { path: '/admin', lazy: AdminDashboard },
          { path: '/admin/users', lazy: AdminUsersIndex },
          { path: '/admin/users/:id', lazy: AdminUsersShow },
          { path: '/admin/shops', lazy: AdminShopsIndex },
          { path: '/admin/shops/create', lazy: AdminShopsCreate },
          { path: '/admin/shops/:id', lazy: AdminShopsShow },
          { path: '/admin/shops/:id/dashboard', lazy: AdminShopsDashboard },
          { path: '/admin/subscriptions', lazy: AdminSubscriptionsIndex },
          { path: '/admin/subscriptions/:id', lazy: AdminSubscriptionsShow },
          { path: '/admin/plans', lazy: AdminPlansIndex },
          { path: '/admin/plans/create', lazy: AdminPlansCreate },
          { path: '/admin/plans/:id/edit', lazy: AdminPlansEdit },
          { path: '/admin/payments', lazy: AdminPaymentsIndex },
          { path: '/admin/finance', lazy: AdminFinanceIndex },
          { path: '/admin/system/logs', lazy: AdminSystemLogs },
          { path: '/admin/system/health', lazy: AdminSystemHealth },
          { path: '/admin/sync', lazy: AdminSync },
          { path: '/admin/quotes', lazy: AdminQuotesIndex },
          { path: '/admin/quotes/:id', lazy: AdminQuotesShow },
          { path: '/admin/announcements', lazy: AdminAnnouncementsIndex },
          { path: '/admin/ai/document-styles', lazy: AdminAiDocumentStyles },
          { path: '/admin/ai/analytics', lazy: AdminAiAnalytics },
          { path: '/admin/messaging', lazy: AdminMessagingDashboard },
          { path: '/admin/messaging/balances', lazy: AdminMessagingBalances },
          { path: '/admin/support', lazy: AdminSupportIndex },
          { path: '/admin/support/:id', lazy: AdminSupportShow },
          { path: '/admin/profile', lazy: AdminProfile },
        ],
      },
    ],
  },
  // Catch-all: auth-aware redirect (Navigate is in-host on local).
  { path: '*', element: <DefaultRedirect />, errorElement: <AppRouteErrorBoundary /> },
];

// =============================================================================
// STOREFRONT tree (shop.velko-pos.com ; local: /shop/*)
// =============================================================================
// Vitrine publique des pages commerciales — AUCUNE auth. Une entrée par type
// de lien (jeton privé /p/:token, page dérivée /:shop/p/:page, page
// principale /:shop), chacune avec panier / commande / confirmation.
const storefrontEntry = (base: string): RouteObject[] => [
  { path: base, lazy: StorePage },
  { path: `${base}/produit/:productKey`, lazy: StoreProductPage },
  { path: `${base}/panier`, lazy: StoreCartPage },
  { path: `${base}/commande`, lazy: StoreCheckoutPage },
  { path: `${base}/confirmation/:reference`, lazy: StoreConfirmationPage },
  { path: `${base}/paiement/retour`, lazy: StorePaymentReturnPage },
  { path: `${base}/mes-commandes`, lazy: StoreOrdersPage },
  { path: `${base}/reservation`, lazy: StoreReservationPage },
  { path: `${base}/mes-reservations`, lazy: StoreReservationsPage },
];

const storefrontTree = (prefix = ''): RouteObject => ({
  errorElement: <AppRouteErrorBoundary />,
  element: <StorefrontLayout />,
  children: [
    { path: prefix || '/', lazy: VelkoBusinessHub },
    { path: `${prefix}/orders/:trackingToken`, lazy: StoreOrderTrackingPage },
    { path: `${prefix}/reservations/:trackingToken`, lazy: StoreReservationTrackingPage },
    ...storefrontEntry(`${prefix}/p/:accessToken`),
    ...storefrontEntry(`${prefix}/:shopSlug/p/:pageSlug`),
    ...storefrontEntry(`${prefix}/:shopSlug`),
    { path: `${prefix}/*`, lazy: StoreNotFound },
  ],
});

const storefrontRoutes: RouteObject[] = [storefrontTree()];

const routesByHost: Record<HostRole, RouteObject[]> = {
  marketing: marketingRoutes,
  app: appRoutes,
  admin: adminRoutes,
  storefront: storefrontRoutes,
  local: [storefrontTree('/shop'), ...localRoutes],
};

const sentryCreateBrowserRouter = Sentry.wrapCreateBrowserRouterV7(createBrowserRouter);

export const router = sentryCreateBrowserRouter(routesByHost[currentHostRole()]);
